import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Avatar,
  Divider,
} from "@mui/material";
import StarIcon from "@mui/icons-material/Star";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import { useNavigate } from "react-router-dom";
import { brandGradient } from "../shared-theme/themePrimitives";
import resolveImageUrl from "../utils/resolveImageUrl";
import { SENTIMENT_COLORS, sentimentLabel } from "../utils/sentiment";

export const CreatorCard = ({ creator, onClick }) => {
  const platform = creator.platforms?.[0]; // Use the first platform if available
  console.log(platform);
  const profile = creator.creator;
  const minPrice = Math.min(
    platform?.reel_price || Infinity,
    platform?.story_price || Infinity,
    platform?.live_price || Infinity,
  );
  const avatarUrl = resolveImageUrl(profile?.profile_image);
  const sentiment = sentimentLabel(platform?.sentiment_label);

  return (
    <Card
      onClick={onClick}
      sx={{
        height: "100%",
        width: "100%",
        p: 0,
        gap: 0,
        overflow: "hidden",
        cursor: "pointer",
        transition: "transform 0.25s ease, box-shadow 0.25s ease",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: 6,
        },
      }}
    >
      {/* Cover */}
      <Box
        sx={{
          height: 72,
          background: avatarUrl
            ? `linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.35)), url(${avatarUrl})`
            : brandGradient,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />

      <CardContent sx={{ px: 2.5, pb: 2.5, pt: 0, textAlign: "center" }}>
        <Avatar
          src={avatarUrl || undefined}
          alt={creator.name}
          sx={{
            width: 84,
            height: 84,
            mx: "auto",
            mt: "-42px",
            border: "4px solid",
            borderColor: "background.paper",
            boxShadow: 2,
            fontSize: "1.75rem",
            fontWeight: 700,
            bgcolor: "secondary.main",
          }}
        >
          {creator.name?.[0]?.toUpperCase()}
        </Avatar>

        <Typography variant="h6" component="div" fontWeight={700} mt={1.5}>
          {creator.name}
        </Typography>

        <Box
          display="flex"
          justifyContent="center"
          flexWrap="wrap"
          gap={0.75}
          mt={1}
        >
          {profile?.top_creator === 1 && (
            <Chip
              icon={<WorkspacePremiumIcon />}
              label="Top Creator"
              size="small"
              color="secondary"
            />
          )}
          {profile?.fast_response === 1 && (
            <Chip
              icon={<FlashOnIcon />}
              label="Responds Fast"
              size="small"
              color="success"
            />
          )}
        </Box>

        {(profile?.rating != null || sentiment) && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={1}
            mt={1.5}
            flexWrap="wrap"
          >
            {profile?.rating != null && (
              <Typography
                variant="body2"
                color="text.secondary"
                display="flex"
                alignItems="center"
                gap={0.5}
              >
                <StarIcon sx={{ fontSize: 16, color: "warning.main" }} />
                {profile.rating}
              </Typography>
            )}
            {sentiment && (
              <Chip
                label={"Sentiment: " + sentiment}
                size="small"
                color={SENTIMENT_COLORS[platform.sentiment_label] || "default"}
                variant="outlined"
                sx={{
                  height: 20,
                  "& .MuiChip-label": { px: 1, fontSize: "0.7rem" },
                }}
              />
            )}
          </Box>
        )}

        {(profile?.content_category || []).length > 0 && (
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            component="div"
            mt={0.5}
          >
            {profile.content_category.join(" • ")}
          </Typography>
        )}

        {minPrice !== Infinity && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Typography variant="subtitle2" color="text.secondary">
              Starting at{" "}
              <Typography
                component="span"
                variant="h6"
                color="primary.main"
                fontWeight={700}
              >
                ${minPrice}
              </Typography>
            </Typography>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default function CreatorGrid({
  creatorTypes,
  creatorList,
  itemToDisplay,
}) {
  const navigate = useNavigate();

  const handleCardClick = (creator) => {
    navigate(`/creator/${creator.id}`, { state: { creator } });
  };

  return (
    <Box sx={{ mt: 6 }}>
      <Box sx={{ textAlign: "center", mb: 3 }}>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 700,
            background: brandGradient,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            display: "inline-block",
          }}
        >
          {creatorTypes}
        </Typography>
        <Typography
          color="text.secondary"
          mt={0.5}
          style={{ fontWeight: "bolder" }}
        >
          Hire top influencers across all platforms
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 3,
          px: { xs: 2, md: 4 },
        }}
      >
        {creatorList.slice(0, itemToDisplay).map((creator, idx) => {
          if (!creator?.creator) return null;

          return (
            <Box
              key={`${creator.name}-${idx}`}
              sx={{
                flex: "1 1 250px",
                maxWidth: "320px",
              }}
            >
              <CreatorCard
                creator={creator}
                onClick={() => handleCardClick(creator)}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}
